/**
 * Shared utility functions for policy rule manipulation.
 * Used by suggestPolicy util, SuggestedPolicyModal, and PolicyEdit.
 */

const MIN_GROUP_PREFIX_LENGTH = 4;
const GENERIC_GROUP_PREFIXES = new Set(['/', '//', 'http://', 'https://', 'www.']);

function longestCommonPrefix(left, right) {
  const a = String(left || '');
  const b = String(right || '');
  const limit = Math.min(a.length, b.length);
  let index = 0;
  while (index < limit && a[index] === b[index]) index += 1;
  return a.slice(0, index);
}

function parseAbsoluteUrl(value) {
  const raw = String(value || '');
  if (!/^https?:\/\//i.test(raw)) return null;
  try {
    return new URL(raw);
  } catch (_) {
    return null;
  }
}

function isMeaningfulCommonPrefix(prefix, left, right) {
  if (prefix.length < MIN_GROUP_PREFIX_LENGTH) return false;

  const leftUrl = parseAbsoluteUrl(left);
  const rightUrl = parseAbsoluteUrl(right);
  if (leftUrl || rightUrl) {
    if (!leftUrl || !rightUrl || leftUrl.origin !== rightUrl.origin) return false;
    return prefix.length > `${leftUrl.origin}/`.length;
  }

  if (GENERIC_GROUP_PREFIXES.has(prefix.toLowerCase())) return false;
  return prefix.replace(/[^a-z0-9]/gi, '').length >= 3;
}

/**
 * Groups indexed policy rules by type/condition/action and a meaningful common field prefix.
 * Each input entry must be shaped as { rule, ruleIndex }.
 */
export function groupRuleEntriesByCommonPrefix(entries) {
  const clusters = [];
  const clustersBySignature = new Map();
  (entries || []).forEach((entry) => {
    const rule = entry.rule || {};
    const signature = `${rule.type || ''}|${rule.condition || ''}|${rule.action || ''}`;
    let bestCluster = null;
    let bestPrefix = '';

    (clustersBySignature.get(signature) || []).forEach((cluster) => {
      const prefix = longestCommonPrefix(cluster.prefix, rule.field);
      if (
        isMeaningfulCommonPrefix(prefix, cluster.entries[0].rule.field, rule.field) &&
        prefix.length > bestPrefix.length
      ) {
        bestCluster = cluster;
        bestPrefix = prefix;
      }
    });

    if (bestCluster) {
      bestCluster.entries.push(entry);
      bestCluster.prefix = bestPrefix;
    } else {
      const cluster = {
        signature,
        prefix: String(rule.field || ''),
        entries: [entry]
      };
      clusters.push(cluster);
      if (!clustersBySignature.has(signature)) {
        clustersBySignature.set(signature, []);
      }
      clustersBySignature.get(signature).push(cluster);
    }
  });

  return clusters.map((cluster) => ({
    groupId: cluster.entries.map((entry) => entry.ruleIndex).join('.'),
    prefix: cluster.prefix,
    entries: cluster.entries,
    isGroup: cluster.entries.length > 1
  }));
}

export function countGroupedRules(rules) {
  const entries = (Array.isArray(rules) ? rules : []).map((rule, ruleIndex) => ({
    rule,
    ruleIndex
  }));
  return groupRuleEntriesByCommonPrefix(entries).length;
}

export function collapseRuleEntriesByCommonPrefix(entries, getGroupEdit = () => ({})) {
  return groupRuleEntriesByCommonPrefix(entries).map((cluster) => {
    const baseRule = cluster.entries[0].rule;
    if (!cluster.isGroup) return baseRule;
    return {
      ...baseRule,
      field: cluster.prefix,
      ...(getGroupEdit(cluster) || {})
    };
  });
}

/**
 * Collapses selected rules while keeping group edits keyed to clusters from the full rule set.
 * This prevents a group's identity from changing when only some of its members are selected.
 */
export function collapseSelectedRuleEntriesByCommonPrefix(entries, options = {}) {
  const {
    isSelected = () => true,
    getRuleEdit = () => ({}),
    getGroupEdit = () => ({})
  } = options;

  return groupRuleEntriesByCommonPrefix(entries).reduce((rules, cluster) => {
    const selectedEntries = cluster.entries
      .filter((entry) => isSelected(entry))
      .map((entry) => ({
        ...entry,
        rule: {
          ...entry.rule,
          ...(getRuleEdit(entry) || {})
        }
      }));

    if (!selectedEntries.length) return rules;

    const groupEdit = cluster.isGroup ? getGroupEdit(cluster) || {} : {};
    if (selectedEntries.length === 1) {
      rules.push({
        ...selectedEntries[0].rule,
        ...groupEdit
      });
      return rules;
    }

    rules.push(
      ...collapseRuleEntriesByCommonPrefix(selectedEntries, () => groupEdit)
    );
    return rules;
  }, []);
}

export function getPolicyGroupUrlIds(group) {
  const urlIds = group && group.urlIds;
  if (Array.isArray(urlIds)) return urlIds.map((id) => String(id));
  if (urlIds instanceof Set) return Array.from(urlIds, (id) => String(id));

  return ((group && group.urls) || [])
    .map((url) => url && url.id)
    .filter((id) => id != null)
    .map((id) => String(id));
}

/**
 * Detect content-level change assets that are too broad to become policy rules,
 * such as "Internal_CSS_#", "In-line_JS_#", and "In-line_Image_#".
 */
export function isContentLevelChangeAssetRule(rule) {
  const field = String((rule && rule.field) || '').trim();
  return /^(?:internal|in[\s_-]*line|external)[\s_-]+[a-z][a-z0-9\s_-]*[\s_-]*#/i.test(
    field
  );
}

/**
 * Creates a unique key for a rule based on type and field.
 * @param {Object} rule - Rule object with type and field properties
 * @returns {string} Normalized key in format "type__field"
 */
export function createRuleKey(rule) {
  return `${(rule.type || '').trim().toLowerCase()}__${(rule.field || '').trim().toLowerCase()}`;
}

/**
 * Normalizes rules to ensure consistent structure with all required fields.
 * @param {Array} rules - Array of rule objects
 * @param {Object} options - Options for normalization
 * @param {boolean} options.includeMetadata - Include label and occurrences (default: false)
 * @returns {Array} Normalized rules array
 */
export function normalizeRules(rules, options = {}) {
  const { includeMetadata = false } = options;
  
  return (rules || []).map((rule, index) => {
    const normalized = {
      type: rule.type,
      condition: rule.condition || 'Contains',
      field: rule.field,
      action: rule.action || 'Do not monitor',
      severity: (index + 1) * 10,
    };
    
    if (includeMetadata) {
      normalized.label = rule.label || '';
      normalized.occurrences = rule.occurrences || [];
    }
    
    return normalized;
  });
}

/**
 * Deduplicates rules by type+field key, merging occurrences.
 * @param {Array} rules - Array of rule objects
 * @returns {Array} Deduplicated and normalized rules
 */
export function dedupeRules(rules) {
  const ruleMap = new Map();
  
  (rules || []).forEach(rule => {
    const key = createRuleKey(rule);
    if (!key || !rule.type || !rule.field) return;
    
    if (!ruleMap.has(key)) {
      ruleMap.set(key, { 
        ...rule, 
        occurrences: rule.occurrences ? [...rule.occurrences] : [] 
      });
    } else {
      const existingRule = ruleMap.get(key);
      if (rule.occurrences) {
        existingRule.occurrences = [...existingRule.occurrences, ...rule.occurrences];
      }
    }
  });
  
  return normalizeRules(Array.from(ruleMap.values()), { includeMetadata: true });
}

/**
 * Filters out rules that already exist in an existing set.
 * @param {Array} incomingRules - New rules to filter
 * @param {Array} existingRules - Existing rules to compare against
 * @returns {Array} Rules that don't exist in existingRules
 */
export function filterNewRules(incomingRules, existingRules) {
  const existingKeys = new Set(
    (existingRules || []).map(rule => createRuleKey(rule))
  );
  
  return (incomingRules || []).filter(rule => {
    const key = createRuleKey(rule);
    return rule.type && rule.field && !existingKeys.has(key);
  });
}

/**
 * Merges new rules into existing rules with proper severity assignment.
 * @param {Array} existingRules - Current rules array
 * @param {Array} newRules - New rules to merge
 * @returns {Array} Combined rules with updated severities
 */
export function mergeRulesWithSeverity(existingRules, newRules) {
  const existing = Array.isArray(existingRules) ? existingRules : [];
  const incoming = Array.isArray(newRules) ? newRules : [];
  
  if (incoming.length === 0) return existing;
  
  const highestSeverity = existing.reduce((maxSeverity, rule) => {
    const currentSeverity = Number(rule.severity) || 0;
    return currentSeverity > maxSeverity ? currentSeverity : maxSeverity;
  }, 0);
  
  const nextSeverity = highestSeverity > 0 ? highestSeverity + 10 : 10;
  
  const mappedNewRules = incoming.map((rule, index) => ({
    type: rule.type,
    condition: rule.condition || 'Contains',
    field: rule.field,
    action: rule.action || 'Do not monitor',
    severity: nextSeverity + index * 10,
  }));
  
  return [...existing, ...mappedNewRules];
}
